/**
 * WorkflowsPage — dynamic category sidebar with full scaffolder integration.
 *
 * Categories and items come from the DB via useLayoutData (same as the docs
 * LayoutPage). Adds everything the old scaffolder-workflows WorkflowsPage had:
 *   - Workflows / Execution History view toggle in the header
 *   - ScaffolderPageContextMenu (actions, editor, templating-extensions links)
 *   - Type and Tag filter dropdowns
 *   - ExecutionHistoryPanel with owner sidebar + paginated task table
 *   - Admin gear icon (visible to backstage-admins only)
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import InputBase from '@mui/material/InputBase';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';

import { Search as SearchIcon, Settings as SettingsIcon, Star as StarIcon, Refresh as RefreshIcon } from '@mui/icons-material';

import ClearIcon from '@mui/icons-material/Clear';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CategoryIcon from '@mui/icons-material/Category';
import FontDownloadIcon from '@mui/icons-material/FontDownload';

import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  StatusOK,
  StatusError,
  StatusPending,
  Link,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import type { ScaffolderTask } from '@backstage/plugin-scaffolder-common';
import type { Entity } from '@backstage/catalog-model';

import { useLayoutData } from '../../hooks/useLayoutData';
import { LayoutSidebar } from '../LayoutPage/LayoutSidebar';
import { LayoutCard } from '../LayoutPage/LayoutCard';
import type { LayoutCategory } from '../../types';
import { ApprovalsPanel } from '../../../pending-approvals/components/ApprovalsPanel/ApprovalsPanel';
import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import type { HarnessExecutionStatus } from '../../../pending-approvals/types';

// ---------------------------------------------------------------------------
// Favourites persistence (keyed independently from the generic LayoutPage)
// ---------------------------------------------------------------------------

const FAVOURITES_KEY = 'oneadvanced:favourites:workflows:v1';

/** Convert a category name to a URL-safe slug, e.g. "SRE & DevOps" → "sre-devops" */
const toSlug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function loadFavourites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVOURITES_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}

function saveFavourites(set: Set<string>): void {
  localStorage.setItem(FAVOURITES_KEY, JSON.stringify([...set]));
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()((theme) => ({
  layout: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  mainContent: { flex: 1, minWidth: 0 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2.5),
    flexWrap: 'wrap' as const,
  },
  searchWrap: {
    flex: 1,
    minWidth: 160,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 1.5),
    backgroundColor: theme.palette.background.paper,
    height: 40,
    boxSizing: 'border-box' as const,
  },
  searchIcon: { color: theme.palette.text.secondary, fontSize: 18, flexShrink: 0 },
  searchInput: { flex: 1, fontSize: 14 },
  filterControl: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 0.5, 0, 1.5),
    backgroundColor: theme.palette.background.paper,
    height: 40,
    boxSizing: 'border-box' as const,
    '& .MuiSelect-root': { fontSize: 14, paddingRight: 28, paddingLeft: 0, paddingTop: 0, paddingBottom: 0 },
    '& .MuiSelect-icon': { right: 4 },
    '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
  },
  viewToggle: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    borderRadius: theme.shape.borderRadius,
    padding: 3,
    marginRight: theme.spacing(1),
  },
  viewToggleBtn: {
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none' as const,
    fontSize: 13,
    fontWeight: 600,
    padding: theme.spacing(0.4, 1.5),
    minWidth: 0,
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)',
    '&:hover': { backgroundColor: 'transparent' },
  },
  viewToggleBtnActive: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
    },
  },
  section: { marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  sectionIconCircle: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sectionTitle: { fontWeight: 700, fontSize: 16, lineHeight: 1.3 },
  sectionSubtitle: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: 2 },
  centred: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1.5),
  },
  // history panel reuses sidebar styles
  historyPaper: {
    '& .MuiPaper-root': { borderRadius: `${4}px !important` },
    '& .MuiCard-root': { borderRadius: `${4}px !important` },
  },
  // sidebar for execution history owner filter
  historySidebar: {
    width: 220,
    flexShrink: 0,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    position: 'sticky' as const,
    top: 0,
  },
  sidebarLabel: {
    display: 'block',
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
  },
  sidebarList: { padding: theme.spacing(0, 0, 1) },
  navItem: {
    width: 'auto',
    borderRadius: 4,
    margin: theme.spacing(0.2, 1),
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  navItemActive: { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244,121,32,0.18)' : '#FDF0F8' },
  navIcon: { minWidth: 28 },
  navIconActive: { color: theme.palette.primary.main, fontSize: 18 },
  navIconInactive: { color: theme.palette.text.secondary, fontSize: 18 },
  navText: { margin: 0, '& span': { fontSize: 13, fontWeight: 500 } },
  navTextActive: { '& span': { color: theme.palette.primary.main, fontWeight: 700 } },
  tableSearchBox: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '2px 8px',
    height: 40,
    gap: 4,
    backgroundColor: theme.palette.background.paper,
  },
}));

// ---------------------------------------------------------------------------
// TaskStatusCell
// ---------------------------------------------------------------------------

function TaskStatusCell({ status }: { status: string }) {
  if (status === 'completed') return <StatusOK>{status}</StatusOK>;
  if (status === 'processing') return <StatusPending>{status}</StatusPending>;
  return <StatusError>{status}</StatusError>;
}

// ---------------------------------------------------------------------------
// HarnessStatusCell
// ---------------------------------------------------------------------------

const HARNESS_STATUS_LABELS: Record<string, string> = {
  ApprovalWaiting: 'awaiting approval',
  ApprovalExpired: 'approval expired',
  ApprovalRejected: 'approval rejected',
  Success: 'success',
  TimedOut: 'timed out',
  ConnectionLost: 'connection lost',
  Expired: 'expired',
};

function HarnessStatusCell({
  status,
  rejectionReason,
  pipelineUrl,
}: {
  status: string;
  rejectionReason: string | null;
  pipelineUrl: string | null;
}) {
  const label = HARNESS_STATUS_LABELS[status] ?? status;
  const inner =
    pipelineUrl ? (
      <a href={pipelineUrl} target="_blank" rel="noreferrer noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
        {label}
      </a>
    ) : (
      <>{label}</>
    );

  let cell: JSX.Element;
  if (status === 'Success') {
    cell = <StatusOK>{inner}</StatusOK>;
  } else if (status === 'ApprovalWaiting') {
    cell = <StatusPending>{inner}</StatusPending>;
  } else {
    cell = <StatusError>{inner}</StatusError>;
  }

  if (!rejectionReason) return cell;
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{rejectionReason}</span>} arrow>
      <span>{cell}</span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// ExecutionHistoryPanel
// ---------------------------------------------------------------------------

function ExecutionHistoryPanel() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const scaffolderApi = useApi(scaffolderApiRef);
  const oneadvancedApi = useApi(oneadvancedApiRef);

  const [tasks, setTasks] = useState<ScaffolderTask[]>([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = (searchParams.get('owner') as 'owned' | 'all') ?? 'owned';
  const setOwnerFilter = (v: 'owned' | 'all') =>
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('owner', v); return next; }, { replace: true });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [harnessStatusMap, setHarnessStatusMap] = useState<Record<string, HarnessExecutionStatus>>({});
  const [workflowFilter, setWorkflowFilter] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchText, setSearchText] = useState('');

  // Fetch on filter/page changes
  useMemo(() => {
    let cancelled = false;
    setHistoryLoading(true);
    scaffolderApi
      .listTasks({ filterByOwnership: ownerFilter, limit: pageSize, offset: page * pageSize })
      .then(result => {
        if (!cancelled) {
          setTasks(result.tasks);
          setTotalTasks(result.totalTasks ?? result.tasks.length);
          setHistoryLoading(false);
          // Batch-fetch Harness status for this page of tasks (indexed by PK — O(page size)).
          const ids = result.tasks.map(t => t.id).filter(Boolean);
          if (ids.length > 0) {
            oneadvancedApi.toolchainApprovals.getExecutionStatus(ids)
              .then(({ data }) => { if (!cancelled) setHarnessStatusMap(data); })
              .catch(() => { /* non-critical enrichment — silently ignore */ });
          }
        }
      })
      .catch(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaffolderApi, oneadvancedApi, ownerFilter, page, pageSize, refreshKey]);

  const workflowOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: string[] = [];
    for (const t of tasks) {
      const label = t.spec.templateInfo?.entity?.metadata.title ??
                    t.spec.templateInfo?.entity?.metadata.name ??
                    (t.spec.templateInfo?.entityRef ?? null);
      if (label && !seen.has(label)) { seen.add(label); options.push(label); }
    }
    return options.sort();
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    const workflowFiltered = workflowFilter
      ? tasks.filter(t => {
          const label = t.spec.templateInfo?.entity?.metadata.title ??
                        t.spec.templateInfo?.entity?.metadata.name ??
                        (t.spec.templateInfo?.entityRef ?? null);
          return label === workflowFilter;
        })
      : tasks;
    const q = searchText.trim().toLowerCase();
    if (!q) return workflowFiltered;
    return workflowFiltered.filter(t => {
      const title = (
        t.spec.templateInfo?.entity?.metadata.title ??
        t.spec.templateInfo?.entity?.metadata.name ??
        t.spec.templateInfo?.entityRef ?? ''
      ).toLowerCase();
      const owner = ((t.spec as any)?.user?.ref ?? '').toLowerCase();
      return t.id.toLowerCase().includes(q) || title.includes(q) || owner.includes(q);
    });
  }, [tasks, workflowFilter, searchText]);

  const tableComponents = useMemo(() => ({
    Toolbar: () => (
      <Box display="flex" alignItems="center" px={2} style={{ minHeight: 56, gap: 8 }}>
        <Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 700, flex: '0 0 auto', marginRight: 8 }}>
          Workflow Executions
        </Typography>
        <Box flex={1} />
        <Autocomplete<string>
          options={workflowOptions}
          value={workflowFilter}
          onChange={(_e, val) => { setWorkflowFilter(val); setPage(0); }}
          style={{ width: 240 }}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="All workflows"
              variant="outlined"
              size="small"
            />
          )}
        />
        <Box className={classes.tableSearchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            placeholder="Search"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ fontSize: 14, width: 160 }}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => setRefreshKey(k => k + 1)}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }), [workflowOptions, workflowFilter, searchText, classes]);

  const columns: TableColumn<ScaffolderTask>[] = [
    {
      title: 'Execution ID',
      field: 'id',
      highlight: true,
      render: row => (
        <Link to={`/create/tasks/${row.id}`} style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {row.id}
        </Link>
      ),
    },
    {
      title: 'Workflow',
      field: 'spec.templateInfo.entity.metadata.title',
      render: row =>
        row.spec.templateInfo?.entity?.metadata.title ??
        row.spec.templateInfo?.entity?.metadata.name ??
        row.spec.templateInfo?.entityRef ??
        '—',
    },
    {
      title: 'Created',
      field: 'createdAt',
      render: row => new Date(row.createdAt).toLocaleString(),
    },
    {
      title: 'Execution Owner',
      field: 'createdBy',
      render: row => {
        const ref = (row.spec as any)?.user?.ref as string | undefined
          ?? harnessStatusMap[row.id]?.requesterRef as string | undefined;
        return ref ? ref.replace(/^[^:]+:[^/]+\//, '') : 'Unknown';
      },
    },
    {
      title: 'Status',
      field: 'status',
      render: row => {
        const h = harnessStatusMap[row.id];
        if (h) return <HarnessStatusCell status={h.harnessStatus} rejectionReason={h.rejectionReason} pipelineUrl={h.pipelineUrl} />;
        return <TaskStatusCell status={row.status} />;
      },
    },
  ];

  const ownerItems = [
    { id: 'owned' as const, label: 'Owned by me', Icon: SettingsIcon },
    { id: 'all' as const, label: 'All executions', Icon: FontDownloadIcon },
  ];

  return (
    <div className={classes.historyPaper}>
      <div className={classes.layout}>
        {/* Owner filter sidebar */}
        <Paper className={classes.historySidebar} elevation={0}>
          <Typography component="span" className={classes.sidebarLabel}>
            Execution Owner
          </Typography>
          <List disablePadding className={classes.sidebarList}>
            {ownerItems.map(({ id, label, Icon }) => {
              const active = ownerFilter === id;
              return (
                <div
                  key={id}
                  className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setOwnerFilter(id); setPage(0); setWorkflowFilter(null); setSearchText(''); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { setOwnerFilter(id); setPage(0); setWorkflowFilter(null); setSearchText(''); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <Icon className={active ? classes.navIconActive : classes.navIconInactive} style={{ fontSize: 18 }} />
                  <Typography className={`${classes.navText} ${active ? classes.navTextActive : ''}`} component="span" style={{ fontSize: 13, fontWeight: active ? 700 : 500 }}>
                    {label}
                  </Typography>
                </div>
              );
            })}
          </List>
        </Paper>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Table<ScaffolderTask>
            title="Workflow Executions"
            isLoading={historyLoading}
            data={visibleTasks}
            columns={columns}
            components={tableComponents}
            totalCount={totalTasks}
            page={page}
            onPageChange={p => setPage(p)}
            onRowsPerPageChange={size => { setPageSize(size); setPage(0); }}
            options={{
              paging: true,
              pageSize,
              pageSizeOptions: [5, 10, 20, 50],
              emptyRowsWhenPaging: false,
              padding: 'dense',
              search: false,
              toolbar: true,
            }}
            onRowClick={(_e, row) => row && navigate(`/create/tasks/${row.id}`)}
            emptyContent={
              <Box display="flex" flexDirection="column" alignItems="center" p={4} style={{ opacity: 0.5, gap: 8 }}>
                <HistoryIcon style={{ fontSize: 48 }} />
                <Typography variant="body1" style={{ fontWeight: 700 }}>No executions found</Typography>
                <Typography variant="body2">There are no workflow executions yet.</Typography>
              </Box>
            }
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkflowsPage
// ---------------------------------------------------------------------------

function getSpecField(entity: Entity, field: string): string {
  if (!entity.spec) return '';
  const val = (entity.spec as Record<string, unknown>)[field];
  return typeof val === 'string' ? val : '';
}

export function WorkflowsPage() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const { allowed: isAdmin } = usePermission({ permission: catalogEntityCreatePermission });

  const { loading, error, categories, itemsByCategory, entityByRef, reload: _reload } =
    useLayoutData('workflows');

  // Fetch ALL Template entities directly so uncategorised ones always appear
  const [allCatalogEntities, setAllCatalogEntities] = useState<Entity[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    catalogApi
      .getEntities({ filter: { kind: 'Template' } })
      .then(result => {
        if (!cancelled) {
          setAllCatalogEntities(result.items);
          setCatalogLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; };
  }, [catalogApi]);

  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') as 'workflows' | 'approvals' | 'history') ?? 'workflows';
  const setView = (v: 'workflows' | 'approvals' | 'history') =>
    setSearchParams(v === 'workflows' ? {} : { view: v }, { replace: true });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const categoryParam = searchParams.get('category') ?? '';
  let selectedId: string;
  if (!categoryParam || categoryParam === 'all') {
    selectedId = 'all';
  } else if (categoryParam === 'favourites') {
    selectedId = 'favourites';
  } else {
    selectedId = categories.find(c => toSlug(c.name) === categoryParam)?.id ?? 'all';
  }
  const setSelectedId = (id: string) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id === 'all') { next.delete('category'); }
      else if (id === 'favourites') { next.set('category', 'favourites'); }
      else { const cat = categories.find(c => c.id === id); if (cat) { next.set('category', toSlug(cat.name)); } else { next.delete('category'); } }
      return next;
    }, { replace: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [favourites, setFavourites] = useState<Set<string>>(loadFavourites);

  // ---------------------------------------------------------------------------
  // Merged entity map — catalog scan + DB-fetched refs
  // ---------------------------------------------------------------------------

  const mergedEntityByRef = useMemo<Record<string, Entity>>(() => {
    const map: Record<string, Entity> = {};
    for (const entity of allCatalogEntities) {
      const ns = entity.metadata.namespace ?? 'default';
      map[`template:${ns}/${entity.metadata.name}`] = entity;
    }
    for (const [ref, entity] of Object.entries(entityByRef)) {
      map[ref] = entity;
    }
    return map;
  }, [allCatalogEntities, entityByRef]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  // Category for each entity ref (first category wins)
  const categoryByRef = useMemo(() => {
    const map: Record<string, LayoutCategory> = {};
    for (const cat of categories) {
      for (const item of (itemsByCategory[cat.id] ?? [])) {
        if (!map[item.entityRef]) map[item.entityRef] = cat;
      }
    }
    return map;
  }, [categories, itemsByCategory]);

  // All catalog entities as flat array for filter dropdowns and counts
  const allEntities = useMemo(() => Object.values(mergedEntityByRef), [mergedEntityByRef]);

  // All unique spec.type values for the type filter dropdown
  const allTypes = useMemo(() => {
    const s = new Set<string>();
    allEntities.forEach(e => { const t = getSpecField(e, 'type'); if (t) s.add(t); });
    return [...s].sort();
  }, [allEntities]);

  // All unique tags for the tag filter dropdown
  const allTags = useMemo(() => {
    const s = new Set<string>();
    allEntities.forEach(e => (e.metadata.tags ?? []).forEach(t => s.add(t)));
    return [...s].sort();
  }, [allEntities]);

  // Sidebar counts (unfiltered)
  const countByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    for (const cat of categories) {
      map[cat.id] = (itemsByCategory[cat.id] ?? []).filter(i => !!mergedEntityByRef[i.entityRef]).length;
    }
    return map;
  }, [categories, itemsByCategory, mergedEntityByRef]);

  const totalCount = allEntities.length;
  const favouriteCount = useMemo(
    () => allEntities.filter(e => {
      const ref = `template:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;
      return favourites.has(ref);
    }).length,
    [allEntities, favourites],
  );

  // Filter predicate
  function matchesFilters(entity: Entity): boolean {
    if (selectedType !== 'all' && getSpecField(entity, 'type') !== selectedType) return false;
    if (selectedTag !== 'all' && !(entity.metadata.tags ?? []).includes(selectedTag)) return false;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const title = (entity.metadata.title ?? entity.metadata.name).toLowerCase();
    const desc = (entity.metadata.description ?? '').toLowerCase();
    const tags = (entity.metadata.tags ?? []).join(' ').toLowerCase();
    return title.includes(q) || desc.includes(q) || tags.includes(q);
  }

  // Visible sections
  const visibleSections = useMemo(() => {
    if (selectedId === 'favourites') {
      const entities = allEntities.filter(e => {
        const ref = `template:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;
        return favourites.has(ref) && matchesFilters(e);
      });
      return entities.length > 0
        ? [{ category: { id: 'favourites', name: 'Favourites', contentType: 'workflows', sortOrder: -1 } as LayoutCategory, entities }]
        : [];
    }

    if (selectedId === 'all') {
      const entities = allEntities.filter(matchesFilters);
      return entities.length > 0
        ? [{ category: { id: 'all', name: 'All Workflows', contentType: 'workflows', sortOrder: 0 } as LayoutCategory, entities }]
        : [];
    }

    // Specific category — show only items assigned to it
    const cat = categories.find(c => c.id === selectedId);
    if (!cat) return [];
    const items = itemsByCategory[cat.id] ?? [];
    const entities = items
      .map(item => mergedEntityByRef[item.entityRef])
      .filter((e): e is Entity => !!e && matchesFilters(e));
    return entities.length > 0 ? [{ category: cat, entities }] : [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEntities, mergedEntityByRef, categories, itemsByCategory, selectedId, searchQuery, selectedType, selectedTag, favourites]);

  // ---------------------------------------------------------------------------
  // Favourite toggle — stores full entity ref
  // ---------------------------------------------------------------------------

  function handleToggleFavourite(entityRef: string) {
    setFavourites(prev => {
      const next = new Set(prev);
      if (next.has(entityRef)) next.delete(entityRef);
      else next.add(entityRef);
      saveFavourites(next);
      return next;
    });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  let workflowsEmptyTitle: string;
  if (searchQuery || selectedType !== 'all' || selectedTag !== 'all') {
    workflowsEmptyTitle = 'No results found';
  } else if (selectedId === 'favourites') {
    workflowsEmptyTitle = 'No favourites yet';
  } else {
    workflowsEmptyTitle = 'No workflows in this category';
  }
  let workflowsEmptySubtitle: string;
  if (selectedId === 'favourites') {
    workflowsEmptySubtitle = 'Star a workflow card to add it to your favourites.';
  } else if (isAdmin) {
    workflowsEmptySubtitle = 'Use the admin panel to add templates to this category.';
  } else {
    workflowsEmptySubtitle = 'Workflows will appear here once they are added to this category.';
  }

  return (
    <Page themeId="tool">
      <Header
        title="Workflows"
        subtitle="Self-service workflows for onboarding, provisioning, and access management."
      >
        {/* Workflows / Execution History toggle */}
        <div className={classes.viewToggle}>
          <Button
            className={`${classes.viewToggleBtn} ${view === 'workflows' ? classes.viewToggleBtnActive : ''}`}
            startIcon={<AppsOutlinedIcon style={{ fontSize: 16 }} />}
            onClick={() => setView('workflows')}
            disableRipple
          >
            Workflows
          </Button>
          <Button
            className={`${classes.viewToggleBtn} ${view === 'approvals' ? classes.viewToggleBtnActive : ''}`}
            startIcon={<AssignmentTurnedInIcon style={{ fontSize: 16 }} />}
            onClick={() => setView('approvals')}
            disableRipple
          >
            Pending Approvals
          </Button>
          <Button
            className={`${classes.viewToggleBtn} ${view === 'history' ? classes.viewToggleBtnActive : ''}`}
            startIcon={<HistoryIcon style={{ fontSize: 16 }} />}
            onClick={() => setView('history')}
            disableRipple
          >
            Execution History
          </Button>
        </div>

        {/* Three-dots menu: scaffolder tools + (admin only) manage layout */}
        <IconButton
          color="inherit"
          aria-label="more options"
          onClick={e => setMenuAnchor(e.currentTarget)}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { setMenuAnchor(null); navigate('/create/edit'); }}>Manage Templates</MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); navigate('/create/templating-extensions'); }}>Templating Extensions</MenuItem>
          <MenuItem onClick={() => { setMenuAnchor(null); navigate('/create/actions'); }}>Installed Actions</MenuItem>
          {isAdmin && <Divider />}
          {isAdmin && (
            <MenuItem onClick={() => { setMenuAnchor(null); navigate('/oneadvanced-admin/layouts/workflows'); }}>
              Manage layout
            </MenuItem>
          )}
        </Menu>
      </Header>

      <Content>
        {/* eslint-disable no-nested-ternary */}
        {view === 'history' ? (
          <ExecutionHistoryPanel />
        ) : view === 'approvals' ? (
          <ApprovalsPanel />
        ) : (loading || catalogLoading) ? (
          <Box className={classes.centred}>
            <CircularProgress />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : error ? (
          <Box className={classes.centred}>
            <Typography variant="body1" style={{ fontWeight: 700 }}>Failed to load</Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Box>
        ) : (
          <div className={classes.layout}>
            {/* Sidebar */}
            <LayoutSidebar
              categories={categories}
              countByCategory={countByCategory}
              totalCount={totalCount}
              favouriteCount={favouriteCount}
              selectedId={selectedId}
              contentTypeLabel="Workflows"
              onSelect={setSelectedId}
            />

            {/* Main content */}
            <div className={classes.mainContent}>
              {/* Search + Type + Tag filters */}
              <div className={classes.toolbar}>
                <div className={classes.searchWrap}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    className={classes.searchInput}
                    placeholder="Search workflows…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    inputProps={{ 'aria-label': 'search workflows' }}
                  />
                  {searchQuery && (
                    <IconButton size="small" style={{ padding: 2 }} onClick={() => setSearchQuery('')}>
                      <ClearIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </div>

                <div className={classes.filterControl}>
                  <Select
                    variant="standard"
                    disableUnderline
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value as string)}
                    displayEmpty
                    renderValue={v => v === 'all' ? 'Type' : (v as string)}
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    {allTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </div>

                <div className={classes.filterControl}>
                  <Select
                    variant="standard"
                    disableUnderline
                    value={selectedTag}
                    onChange={e => setSelectedTag(e.target.value as string)}
                    displayEmpty
                    renderValue={v => v === 'all' ? 'Tags' : (v as string)}
                  >
                    <MenuItem value="all">All Tags</MenuItem>
                    {allTags.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </div>
              </div>

              {/* Sections */}
              {visibleSections.length === 0 ? (
                <Box className={classes.centred}>
                  <CategoryIcon style={{ fontSize: 48 }} />
                  <Typography variant="body1" style={{ fontWeight: 700 }}>
                    {workflowsEmptyTitle}
                  </Typography>
                  <Typography variant="body2">
                    {workflowsEmptySubtitle}
                  </Typography>
                </Box>
              ) : (
                visibleSections.map(({ category, entities }) => (
                  <div key={category.id} className={classes.section}>
                    {/* Section header */}
                    <div className={classes.sectionHeader}>
                      <div
                        className={classes.sectionIconCircle}
                        style={{ backgroundColor: `${category.accentColor ?? '#E6007E'}18` }}
                      >
                        {category.id === 'favourites'
                          ? <StarIcon style={{ fontSize: 22, color: '#F59E0B' }} />
                          : <CategoryIcon style={{ fontSize: 22, color: category.accentColor ?? '#E6007E' }} />
                        }
                      </div>
                      <div>
                        <Typography className={classes.sectionTitle}>{category.name}</Typography>
                        {category.description && (
                          <Typography className={classes.sectionSubtitle}>{category.description}</Typography>
                        )}
                      </div>
                    </div>

                    <Grid container spacing={2}>
                      {entities.map(entity => {
                        const entityRef = `template:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
                        return (
                          <Grid key={entityRef} item xs={12} sm={6} md={4} lg={3}>
                            <LayoutCard
                              entity={entity}
                              contentType="workflows"
                              category={categoryByRef[entityRef]}
                              isFavourite={favourites.has(entityRef)}
                              onToggleFavourite={handleToggleFavourite}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {/* eslint-enable no-nested-ternary */}
      </Content>
    </Page>
  );
}
